import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import * as _ from "lodash";
import { formatComma } from "../../utils/stringFormat";
import { PATH } from "../../routes/Routes";
import { DELETE } from "../../utils/HttpClient";
import { Toast } from "../../utils/Toast";
import MainHeader from "../../components/common/MainHeader";
import ReportExportButton from "../../components/common/ReportExportButton";
import { URL_POST_BAKING, URL_REPORT } from "../../constance/urls";
import ConfirmModal from "../../components/common/ConfirmModal";
import TSTable from "../../components/common/TSTable";
import { Layout, List, Tooltip, Form, Row, Col, DatePicker, Input, Space, Button } from "antd";
import { DATE_FORMAT, HUMAN_DATE_FORMAT } from "../../constance/strings";
import dayjs from 'dayjs';
import Title from "antd/es/typography/Title";
import { Content } from "antd/es/layout/layout";
import { useDebounce } from "use-debounce";
import { DeleteFilled, FileTextFilled } from "@ant-design/icons";
import { BLUE_COLOR } from "../../constance/color";
import ColorButton from "../../components/common/ColorButton";

export default function PostBakingWasteWoodCountListView() {

  const [form] = Form.useForm();
  const { t } = useTranslation();
  const history = useHistory();
  const tableRef = React.useRef();
  const [loading, setLoading] = React.useState(false);
  const dateRange = Form.useWatch('dateRange', form);
  const ovenFilter = Form.useWatch('oven_no', form);
  const [deleteConfirm, setDeleteConfirm] = React.useState(null);
  const haveDateRange = _.get(dateRange, 0, null) && _.get(dateRange, 1, null)
  const { RangePicker } = DatePicker;
  const [debouncedText] = useDebounce(ovenFilter, 500);

  React.useEffect(() => {
    if (tableRef.current) {
      tableRef.current.fetch();
    }
  }, [dateRange, debouncedText])

  const columns = React.useMemo(() => [
    {
      title: t('datas.index'),
      dataIndex: 'index',
      render: (__, original, index) => <div>{index + 1}</div>
    },
    {
      title: t('post_baking_waste_wood_count.group_date'),
      dataIndex: 'post_baking_group_date',
      render: (value) => new Date(value).toLocaleDateString('en-GB'),
    },
    {
      title: t('post_baking_waste_wood_count.count_date'),
      dataIndex: 'created',
      render: (value) => new Date(value).toLocaleDateString('en-GB'),
    },
    {
      title: t('post_baking_waste_wood_count.oven'),
      dataIndex: 'ovens',
      render: (value) => {
        if (!value || !value.length) {
          return t('post_baking_waste_wood_count.all_oven')
        }
        return value.join(', ') || '-'
      },
    },
    {
      title: t('wood_size'),
      dataIndex: 'wood_size_amount_map_list',
      render: (value) => {
        return value ? (
          <List>
            {value.filter(data => _.get(data, 'amount', 0)).map(data => (
              <List.Item >
                {_.get(data, 'wood_code', '')} - {`(${formatComma(_.get(data, 'amount', 0))})`} {formatComma(_.get(data, 'volume', 0), 4)}
              </List.Item>
            ))}
          </List>
        ) : '-'
      }

    },
    {
      title: t('post_baking_waste_wood_input.waste_wood_group'),
      render: (__, original) => {
        const value = _.get(original, 'wood_size_amount_map_list', [])
        return value ? (
          <List>
            {value.map(v => _.get(v, 'waste_wood_group', '') &&
              <List.Item>{t(`post_baking_waste_wood_input.waste_wood_groups.${_.get(v, 'waste_wood_group', '')}`)}</List.Item>
            )}
          </List>
        ) : '-'
      }
    },
    {
      title: t('post_baking_waste_wood_input.waste_wood_type'),
      render: (__, original) => {
        const value = _.get(original, 'wood_size_amount_map_list', [])
        return value ? (
          <List>
            {value.map(v => _.get(v, 'waste_wood_type', '') &&
              <List.Item>{t(`post_baking_waste_wood_input.waste_wood_types.${_.get(v, 'waste_wood_type', '')}`)}</List.Item>
            )}
          </List>
        ) : '-'
      }
    },
    {
      title: t('workflows.action'),
      dataIndex: 'id',
      render: (__, original) => (
        <div style={{ textAlign: 'center' }}>
          <Space>
            <ColorButton
              variant="outlined"
              color='primary'
              override={BLUE_COLOR}
              icon={<FileTextFilled />}
              loading={loading}
              onClick={() => history.push(`${PATH.POST_BAKING_WASTE_WOOD_COUNT_REPORT}${original.id}`)} >
              {!_.get(original, 'interfaced', false) ? t('edit') : t('detail')}
            </ColorButton>
            {!_.get(original, 'interfaced', false) &&
              <Button
                variant="outlined"
                color="danger"
                icon={<DeleteFilled />}
                loading={loading}
                onClick={() => setDeleteConfirm(original.id)} >{t('delete')}</Button>
            }
          </Space>
        </div>
      ),
    },

  ])

  const handleDelete = async () => {
    setLoading(true);
    try {
      await DELETE(`${URL_POST_BAKING.POST_BAKING_WASTE_WOOD_COUNT}${deleteConfirm}/`)
      if (tableRef.current) {
        tableRef.current.fetch();
      }
      Toast.success(`${t('delete')} ${t('success')}`)
    } catch (error) {
      Toast.error(error.errorMessages);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <Layout>
        <MainHeader />
        <Content style={{ margin: '24px', marginTop: '7em' }}>
          <Title level={2}>{t('menus.report_menus.waste_woods.post_baking_waste_wood_count')}</Title>

          <Form form={form}>
            <Row gutter={16} >
              <Col sm={24} md={24} lg={8}>
                <Form.Item label={t('post_baking_waste_wood_count.group_date')} name={"dateRange"}>
                  <RangePicker
                    format={HUMAN_DATE_FORMAT}
                    placeholder={[
                      t('searchs.placeholders.startDate'),
                      t('searchs.placeholders.endDate'),
                    ]}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col sm={24} md={24} lg={8} >
                <Form.Item label={t('post_baking_waste_wood_count.oven_no')} name={"oven_no"}>
                  <Input placeholder={t('all')} allowClear type="number" />
                </Form.Item>
              </Col>
              <Col sm={24} md={24} lg={8} style={{ textAlign: 'right', marginBottom: '1rem' }}>
                <Tooltip title={!haveDateRange ? t('searchs.placeholders.date') : ""}>
                  <span >
                    <ReportExportButton
                      url={`${URL_REPORT.POST_BAKING_WASTE_WOOD_COUNT_REPORT}excel/`}
                      title={t('menus.report_menus.waste_woods.post_baking_waste_wood_count')}
                      type='excel'
                      disabled={!haveDateRange}
                      params={{
                        oven: ovenFilter,
                        date_after: _.get(dateRange, 0, null) ? dayjs(dateRange[0]).format(DATE_FORMAT) : null,
                        date_before: _.get(dateRange, 1, null) ? dayjs(_.get(dateRange, 1)).format(DATE_FORMAT) : null,
                      }}
                    />
                  </span>
                </Tooltip>
              </Col>
            </Row>
          </Form>

          <TSTable
            ref={tableRef}
            columns={columns}
            url={URL_POST_BAKING.POST_BAKING_WASTE_WOOD_COUNT}
            params={{
              date_after: _.get(dateRange, 0, null) ? dayjs(dateRange[0]).format(DATE_FORMAT) : null,
              date_before: _.get(dateRange, 1, null) ? dayjs(_.get(dateRange, 1)).format(DATE_FORMAT) : null,
              oven: debouncedText,
            }}
          />

        </Content>
      </Layout>

      <ConfirmModal
        open={deleteConfirm != null}
        onClose={() => setDeleteConfirm(null)}
        title={`${t('confirms.delete_title')}`}
        content={`${t('confirms.delete_content')}`}
        onConfirm={handleDelete} />

    </div>
  )
}

